import { gsap } from "gsap";
import HeroAnimationTimeline from "./HeroAnimationTimeline.js";

export default class HeroAnimationController {
    constructor(parent) {
        this.html = {}
        this.parent = parent;

        // Check if we should use instant intro (when there's an anchor)
        this.useInstantIntro = !!window.location.hash;

        this.heroEl = document.getElementById('hero')
        this.logoEl = this.heroEl.querySelector('.hero--logo')
        this.titleEl = this.heroEl.querySelector('.hero--title')
        this.headingEl = this.heroEl.querySelector('.hero--heading')

        this.subnavEl = document.querySelector('.page-nav')

        this.html.subnav = {
            el: this.subnavEl
        }

        this.html.subnav.el?.classList.add('invisible')

        this.html.hero = {
            el: this.heroEl,
            logo: this.logoEl,
            title: this.titleEl,
            heading: this.headingEl
        }

        this.heroThesisEl = document.querySelector('.scroll-block.thesis')
        this.heroThesisBackgroundEl = document.querySelector('.scroll-block.thesis .scroll-block-bg')
        this.thesisTextHolderEl = document.querySelector('.scroll-content .text-holder')
        this.thesisTextEl = this.heroThesisEl.querySelectorAll('.text-holder p')

        this.html.slidePanel = {
            el: this.heroThesisEl,
            background: this.heroThesisBackgroundEl,
            thesisTextHolderEl: this.thesisTextHolderEl,
            thesisTextEl: this.thesisTextEl
        }

        this.landingEl = document.getElementById('landing')
        this.landingContainerEl = this.landingEl.querySelector(':scope > .container')
        this.landingHeader = this.landingEl.querySelector('.header')
        this.landingHeadingEls = this.landingEl.querySelectorAll('.header > *')
        this.cards = this.landingEl.querySelectorAll('.card-item')
        this.landingBackgroundEl = this.landingEl.querySelector('.gw-grid')
        this.landingVerticalLines = this.landingEl.querySelector('.gw-vertical-lines')
        this.animatedGridEl = document.querySelector('.grid-animation-container')

        this.html.landing = {
            el: this.landingEl,
            container: this.landingContainerEl,
            header: this.landingHeader,
            heading: this.landingHeadingEls,
            cards: this.cards,
            grid: this.landingBackgroundEl,
            verticalLines: this.landingVerticalLines,
            animatedGridEl: this.animatedGridEl
        }

        gsap.set(this.html.landing.header, {
            y: ((this.html.landing.el.clientHeight - this.html.landing.header.clientHeight) / 2) - parseInt(window.getComputedStyle(this.html.landing.header).marginBottom) - 50,
        })

        gsap.set(this.html.landing.heading, {
            y: 50,
            autoAlpha: 0
        })

        gsap.set(this.html.landing.cards, {
            y: 50,
            autoAlpha: 0
        })

        gsap.set(this.html.landing.grid, {
            autoAlpha: 0
        })

        gsap.set(this.html.landing.verticalLines, {
            scaleY: 0,
            transformOrigin: 'top'
        })

        this.latestEl = document.querySelector('.gm2-latest')
        this.latestHeader = this.latestEl.querySelector('.header')
        this.latestHeadingEls = this.latestEl.querySelectorAll('.header > *')
        this.latestSlider = this.latestEl.querySelector('.swiper-latest-growth')

        this.html.latest = {
            el: this.latestEl,
            header: this.latestHeader,
            heading: this.latestHeadingEls,
            slider: this.latestSlider
        }

        gsap.set(this.html.latest.heading, {
            y: 50,
            autoAlpha: 0
        })

        gsap.set(this.html.latest.slider, {
            y: 50,
            autoAlpha: 0
        })

        this.evolutionEl = document.querySelector('.gm2-player')
        this.evolutionHeader = this.evolutionEl.querySelector('.heading')
        this.evolutionHeadingEls = this.evolutionEl.querySelectorAll('.heading > *')
        this.evolutionBackgroundEl = this.evolutionEl.querySelector('.gm2-player-background')
        this.evolutionVideosContainer = this.evolutionEl.querySelector('.videos-container')

        this.html.evolution = {
            el: this.evolutionEl,
            header: this.evolutionHeader,
            heading: this.evolutionHeadingEls,
            background: this.evolutionBackgroundEl,
            videos: this.evolutionVideosContainer
        }

        gsap.set(this.html.evolution.heading, {
            y: 50,
            autoAlpha: 0
        })

        gsap.set(this.html.evolution.videos, {
            y: 50,
            autoAlpha: 0
        })

        gsap.set(this.html.evolution.background, {
            autoAlpha: 0
        })

        this.isInitialAnimation = true;
        this.initializeState();
    }

    initializeState() {
        this.timelines = new HeroAnimationTimeline(this.html);
    }

    createTimeline(index, isForward = true) {
        const tl = gsap.timeline();
        const currentIndex = this.getCurrentIndex();
        
        // If the source and destination indices are the same, return an empty timeline
        if (currentIndex === index && !this.isInitialAnimation) {
            console.log('Source and destination indices are the same - no animation needed');
            return tl;
        }

        // Always use sequential timeline for initial animation
        if (this.isInitialAnimation) {
            this.isInitialAnimation = false;
            
            // For index 0 (hero intro), check if we should use instant version
            if (index === 0 && this.useInstantIntro) {
                console.log('Using instant hero intro because of anchor in URL');
                this.timelines.timelineForwardAppearHeroInstant(tl);
            } else {
                // Normal animation flow
                this.timelines.createTimeline(tl, index, isForward);
            }
            return tl;
        }

        // Special case: moving from hero (index 0) to any panel
        if (currentIndex === 0 && index > 1) {
            console.log('Special transition from hero to panel', index);
            
            // First add the transition from hero to compendium
            this.timelines.timelineForwardRetractHero(tl);
            this.timelines.timelineForwardAppearCompendiumInstant(tl);
            
            // If we need to go further than compendium (index 1)
            if (index === 2) {
                this.timelines.timelineForwardAppearCompendiumCards(tl);
            } else if (index === 3) {
                this.timelines.timelineCompendiumToLatestInstant(tl);
                this.timelines.timelineForwardAppearLatest(tl);
            } else if (index === 4) {
                this.timelines.timelineIntermediateCompendiumToLastPanel(tl);
                this.timelines.timelineForwardAppearEvolution(tl);
            }
            
            return tl;
        }

        // If moving sequentially, use the original timeline creation
        if (Math.abs(index - currentIndex) === 1) {
            console.log('createTimeline');
            this.timelines.createTimeline(tl, index, isForward);
            return tl;
        }

        // For non-sequential moves, use the new function
        this.timelines.createNonSequentialTimeline(tl, currentIndex, index);
        return tl;
    }

    getCurrentIndex() {
        return this.parent.state.currentIndex;
    }

    // Reset all animations
    reset() {
        this.isInitialAnimation = true;
        this.initializeState();
    }

    // Destroy and cleanup
    destroy() {
    }
}
